/**
 * Sprint 0150 — Host picker dropdown.
 *
 * Replaces the free-text "Person to Meet" field. Backed by
 * GET /visitors/hosts which returns only employees flagged
 * can_host_visitors=true. Searches against name + emp_code.
 *
 * Renders each option as: photo avatar + name + (designation • dept).
 */
import { Avatar, Select, Space, Tag, Typography } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'

import { visitorsApi } from '@/features/gate/api'

const { Text } = Typography

interface Props {
  value?: string | null
  onChange?: (value: string | null) => void
  plantId?: string | null
  /** Render as required (red asterisk shown by parent Form.Item) */
  disabled?: boolean
}

export default function HostPicker({ value, onChange, plantId, disabled }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['hosts', plantId],
    queryFn: () => visitorsApi.listHosts({ plant_id: plantId ?? undefined }),
  })
  const hosts = data ?? []

  return (
    <Select<string>
      value={value ?? undefined}
      onChange={(v) => onChange?.(v ?? null)}
      loading={isLoading}
      disabled={disabled}
      showSearch
      placeholder="Search authorised hosts by name or code…"
      optionFilterProp="label"
      filterOption={(input, opt) => {
        const lc = input.toLowerCase()
        const o = opt as { label?: string; raw?: { full_name?: string; emp_code?: string } }
        return (
          (o.label ?? '').toLowerCase().includes(lc)
          || (o.raw?.full_name ?? '').toLowerCase().includes(lc)
          || (o.raw?.emp_code ?? '').toLowerCase().includes(lc)
        )
      }}
      options={hosts.map((h) => ({
        value: h.id,
        label: `${h.full_name} (${h.emp_code})`,
        raw: h,
      }))}
      optionRender={(opt) => {
        const h = (opt.data as { raw?: typeof hosts[number] }).raw
        if (!h) return null
        return (
          <Space>
            <Avatar
              src={h.photo_url || undefined}
              icon={<UserOutlined />}
              size={32}
              style={{ background: h.photo_url ? undefined : '#bfbfbf' }}
            />
            <div>
              <div>
                <Text strong>{h.full_name}</Text>
                <Tag style={{ marginLeft: 6 }}>{h.emp_code}</Tag>
              </div>
              {(h.designation_name || h.department_name) && (
                <div style={{ fontSize: 11, color: '#888' }}>
                  {[h.designation_name, h.department_name].filter(Boolean).join(' • ')}
                </div>
              )}
            </div>
          </Space>
        )
      }}
      notFoundContent={
        isLoading
          ? 'Loading hosts…'
          : 'No authorised hosts found. Ask admin to flag staff as "can host visitors" in HR.'
      }
    />
  )
}
